import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import useShouldAnimate, {
  useShouldAnimateOnMount,
} from '../../../hooks/useShouldAnimate'
import { Box } from '../../../shared_components/UI/Box'
import { formatAccountAbbreviation } from '../../../shared_components/ConnectedWallet'
import { selectAccount } from '../../../modules/web3/selectors'
import {
  CenterAlignedTableDivision,
  CenterAlignedTableHeader,
  Column,
  Table,
  TableDivision,
  TableHeader,
  TableRow,
  You,
} from './styles'
import LoadingState from './LoadingState'
import useLeaderboard from '../useLeaderboard'
import useUserSlicedLeaderboard from '../useUserSlicedLeaderboard'
import { usePagination } from '../../../hooks/usePagination'
import { ThemedLink } from '../../../shared_components/ThemedButton'
import { H2, P } from '../../../shared_components/Library/Typeography'
import styled from 'styled-components'

function Leaderboard({ projectId }: { projectId?: number }) {
  const {
    leaderboard,
    loading: loadingLeaderboard,
    handleFetchLeaderboard,
  } = useLeaderboard(projectId)
  const { userSlicedLeaderboard, loading: loadingUserSlicedLeaderboard } =
    useUserSlicedLeaderboard(projectId)

  const { limit, offset } = usePagination({
    defaultLimit: 10,
  })

  useEffect(() => {
    handleFetchLeaderboard({ projectId, limit, offset })
  }, [handleFetchLeaderboard, limit, offset, projectId])

  return (
    <Box dc>
      <H2 s="XL" fw="semibold" padB="m">
        Leaderboard
      </H2>
      <LeaderboardTable
        loading={loadingLeaderboard}
        total={leaderboard?.total ?? 0}
        leaderboard={leaderboard?.rows}
      />
      {userSlicedLeaderboard?.rows?.length > 0 && (
        <>
          {/* <H2 s="XL" fw="semibold" padT="m" padB="m">
            Your Leaderboard Standing
          </H2> */}
          <YourPositionDivider>
            <YourPositionText>Your position</YourPositionText>
          </YourPositionDivider>
          <LeaderboardTable
            loading={loadingUserSlicedLeaderboard}
            leaderboard={userSlicedLeaderboard?.rows}
            headers={false}
          />
        </>
      )}
    </Box>
  )
}

const defaultHeaders = ['Rank', 'Account', 'XP', 'Level']

export function LeaderboardTable({
  loading,
  leaderboard,
  total,
  headers = defaultHeaders,
  animateRowsOn = 'visible',
}: {
  loading: boolean
  leaderboard: any[]
  total?: number
  headers?: false | string[]
  animateRowsOn?: 'mount' | 'visible'
}) {
  if (!loading && total === 0) {
    return (
      <Box dc ac g={24} marginTop={32}>
        <P>No XP earned yet. Be the first!</P>
        <ThemedLink to="/drops" $styleType="primary">
          Earn XP
        </ThemedLink>
      </Box>
    )
  }

  return (
    <Table>
      <colgroup>
        <Column columnWidth="5%" />
        {/* <Column columnWidth="15%" /> */}
        <Column columnWidth="55%" />
        <Column columnWidth="15%" />
        <Column columnWidth="15%" />
      </colgroup>
      <tbody>
        {headers && (
          <tr>
            {headers.includes('Rank') && (
              <CenterAlignedTableHeader key="header-rank">
                Pos
              </CenterAlignedTableHeader>
            )}
            {/* <TableHeader key="header-avatar" /> */}
            {headers.includes('Account') && (
              <TableHeader key="header-account">Account</TableHeader>
            )}
            {headers.includes('XP') && (
              <CenterAlignedTableHeader key="header-xp" themeColor>
                XP
              </CenterAlignedTableHeader>
            )}
            {headers.includes('Level') && (
              <TableHeader key="header-level" textAlign="right">
                Lvl
              </TableHeader>
            )}
          </tr>
        )}
        {loading &&
          Array(10)
            .fill(null)
            .map((_, i) => <LoadingState key={i} />)}
        {!loading &&
          leaderboard?.length > 0 &&
          leaderboard?.map((record, index) => (
            <LeaderboardRow
              key={`leaderboard-row-${index}`}
              record={record}
              showBorder={index !== leaderboard.length - 1}
              headers={headers || defaultHeaders}
              animateRowsOn={animateRowsOn}
            />
          ))}
      </tbody>
    </Table>
  )
}

function LeaderboardRow({
  record,
  showBorder,
  headers,
  animateRowsOn,
}: {
  record: any
  showBorder: boolean
  headers: string[]
  animateRowsOn: 'mount' | 'visible'
}) {
  const loggedInAccount = useSelector(selectAccount)
  const { ref, shouldAnimate: shouldAnimateOnVisible } = useShouldAnimate()
  const { shouldAnimate: shouldAnimateOnMount } = useShouldAnimateOnMount()
  const { rank, account, totalXp, level } = record

  const isLoggedInAccount =
    account.toLowerCase() === loggedInAccount?.toLowerCase()

  return (
    <TableRow
      ref={ref}
      showBorder={showBorder}
      isVisible={
        animateRowsOn === 'visible'
          ? shouldAnimateOnVisible
          : shouldAnimateOnMount
      }
    >
      {headers.includes('Rank') && (
        <CenterAlignedTableDivision highlight={isLoggedInAccount}>
          <Box ac jc fu aw g={4}>
            {isLoggedInAccount && <RankHighlightIcon />}
            {rank}
          </Box>
        </CenterAlignedTableDivision>
      )}
      {/* <TableDivision>
        <Box>{avatar}</Box>
      </TableDivision> */}
      {headers.includes('Account') && (
        <TableDivision highlight={isLoggedInAccount} bold>
          {formatAccountAbbreviation(account)}
          {isLoggedInAccount && <You>(You)</You>}
        </TableDivision>
      )}
      {headers.includes('XP') && (
        <CenterAlignedTableDivision highlight={isLoggedInAccount}>
          {totalXp}
        </CenterAlignedTableDivision>
      )}
      {headers.includes('Level') && (
        <TableDivision highlight={isLoggedInAccount} textAlign="right">
          {level}
        </TableDivision>
      )}
    </TableRow>
  )
}

function RankHighlightIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="8"
      height="9"
      viewBox="0 0 8 9"
      fill="none"
    >
      <path
        d="M7.5 3.63397C8.16667 4.01887 8.16667 4.98113 7.5 5.36603L2.25 8.39711C1.58333 8.78201 0.75 8.30089 0.75 7.53109L0.75 1.46891C0.75 0.699111 1.58333 0.217985 2.25 0.602885L7.5 3.63397Z"
        fill="#BBF6E2"
      />
    </svg>
  )
}

const YourPositionDivider = styled.div`
  display: flex;
  padding: var(--units-4, 4px) 0px;

  justify-content: center;
  align-items: center;
  gap: 16px;

  align-self: stretch;
  width: 100%;

  &::before,
  &::after {
    content: '';
    flex: 1 0 0;
    border-bottom: var(--units-1, 1px) dashed var(--stroke-primary, #FFF);
  }

  &::before {
    margin-right: 12px;
  }

  &::after {
    margin-left: 12px;
  }
`;

const YourPositionText = styled.span`
  color: var(--text-secondary, #B1B1B1);
  text-align: center;

  /* Web/Text/S/Regular */
  font-family: var(--type-family-space-grotesk, "Space Grotesk");
  font-size: var(--Text-S-S-size, 12px);
  font-style: normal;
  font-weight: var(--type-weight-Regular, 300);
  line-height: var(--Text-S-S-line-height, 16px); /* 133.333% */
`;

export default Leaderboard
